import React, { useCallback, useRef, useState } from "react";
import { Box, Text } from "@chakra-ui/react";
import {
	DndContext,
	type DragEndEvent,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ColumnHeaderCell } from "./ColumnHeaderCell";
import { GhostAddColumn } from "./GhostAddColumn";
import { ColumnEditPopover } from "./ColumnEditPopover";
import { ColumnContextMenu } from "./ColumnContextMenu";
import {
	BLANK_ROWS,
	COLUMN_WARNING_THRESHOLD,
	MAX_COLUMNS,
	SAMPLE_CELL_LABELS,
} from "./column-types";
import type {
	ColumnConfig,
	DefaultRowConfig,
	QuoteColumnType,
} from "./column-types";

export interface SpreadsheetGridProps {
	columns: ColumnConfig[];
	defaultRows?: DefaultRowConfig[];
	/**
	 * When true, preview rows are rendered in italic muted text to signal they
	 * are placeholder examples (used when no real row data has been supplied
	 * yet — see "Custom"/"Other" presets in CreateQuoteTableModal).
	 */
	exampleMode?: boolean;
	getDisabledReason: (type: QuoteColumnType) => string;
	isTypeDisabled: (type: QuoteColumnType, currentColType?: QuoteColumnType) => boolean;
	/**
	 * Optional. When supplied, types for which this returns `true` are removed
	 * from the column-type chooser entirely (rather than just disabled). Use
	 * for feature surfaces with a narrower type set (e.g. Schedule of Rates)
	 * where unsupported types shouldn't be visible at all.
	 */
	isTypeHidden?: (type: QuoteColumnType, currentColType?: QuoteColumnType) => boolean;
	onAddColumn: () => void;
	onDuplicateColumn: (localId: string) => void;
	onInsertColumn: (localId: string, position: "left" | "right") => void;
	onMoveColumn: (localId: string, direction: "left" | "right") => void;
	onRemoveColumn: (localId: string) => "removed" | "needs-confirmation" | "blocked";
	onReorderColumns: (sourceIndex: number, destIndex: number) => void;
	onUpdateLabel: (localId: string, label: string) => void;
	onUpdateType: (localId: string, type: QuoteColumnType) => void;
	/**
	 * Optional render-prop for the table body. When provided, replaces the
	 * default preview-row body with consumer-rendered content (still inside
	 * `<tbody role="rowgroup">`). When omitted, the default preview rows
	 * derived from `defaultRows` (or `BLANK_ROWS`) and `SAMPLE_CELL_LABELS`
	 * are rendered.
	 */
	renderBody?: (columns: ColumnConfig[]) => React.ReactNode;
}

interface ContextMenuState {
	localId: string;
	position: { x: number; y: number };
}

const STICKY_CELL_STYLE: React.CSSProperties = {
	position: "sticky",
	left: 0,
	zIndex: 10,
	background: "white",
};

/**
 * Main grid container for the quote-table editor. Renders the column-header
 * row (drag-reorderable via @dnd-kit), the trailing ghost "+" cell, and a few
 * preview body rows so the procurer sees how the table will look to suppliers.
 *
 * State rule: at most ONE of `openPopoverId` / `contextMenu` is non-null. The
 * setters below are routed through helpers that close the other surface.
 *
 * The `ColumnEditPopover` and `ColumnContextMenu` are rendered OUTSIDE the
 * `<table>` element to avoid invalid DOM nesting (Popovers / portals inside
 * `<th>` cause hydration warnings in some browsers).
 */
export const SpreadsheetGrid = ({
	columns,
	defaultRows,
	getDisabledReason,
	isTypeDisabled,
	isTypeHidden,
	onAddColumn,
	onDuplicateColumn,
	onInsertColumn,
	onMoveColumn,
	onRemoveColumn,
	onReorderColumns,
	onUpdateLabel,
	onUpdateType,
	renderBody,
	exampleMode = false,
}: SpreadsheetGridProps) => {
	const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
	const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
	const headerRefs = useRef<Map<string, HTMLElement>>(new Map());

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
	);

	const openPopover = useCallback((localId: string) => {
		setContextMenu(null);
		setOpenPopoverId(localId);
	}, []);

	const closePopover = useCallback(() => setOpenPopoverId(null), []);

	const openContextMenu = useCallback(
		(localId: string, x: number, y: number) => {
			setOpenPopoverId(null);
			setContextMenu({ localId, position: { x, y } });
		},
		[]
	);

	const closeContextMenu = useCallback(() => setContextMenu(null), []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			const sourceIndex = columns.findIndex((c) => c.localId === active.id);
			const destIndex = columns.findIndex((c) => c.localId === over.id);
			if (sourceIndex < 0 || destIndex < 0) return;
			onReorderColumns(sourceIndex, destIndex);
		},
		[columns, onReorderColumns]
	);

	const sortableIds = columns.map((c) => c.localId);

	const openPopoverColumn =
		openPopoverId !== null
			? columns.find((c) => c.localId === openPopoverId) ?? null
			: null;
	const contextMenuColumn =
		contextMenu !== null
			? columns.find((c) => c.localId === contextMenu.localId) ?? null
			: null;
	const contextMenuIndex =
		contextMenu !== null
			? columns.findIndex((c) => c.localId === contextMenu.localId)
			: -1;

	const showWarning =
		columns.length >= COLUMN_WARNING_THRESHOLD && columns.length < MAX_COLUMNS;

	return (
		<>
			<Box overflowX="auto" data-testid="spreadsheet-grid-scroll">
				<Box
					as="table"
					role="grid"
					data-testid="spreadsheet-grid"
					sx={{
						borderCollapse: "separate",
						borderSpacing: 0,
						minWidth: "100%",
					}}
				>
					<thead data-testid="spreadsheet-grid-header">
						<tr role="row">
							<th
								role="columnheader"
								style={STICKY_CELL_STYLE}
								data-testid="row-number-header"
							>
								#
							</th>
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext
									items={sortableIds}
									strategy={horizontalListSortingStrategy}
								>
									{columns.map((column, index) => (
										<th
											key={column.localId}
											role="columnheader"
											ref={(el) => {
												if (el) headerRefs.current.set(column.localId, el);
												else headerRefs.current.delete(column.localId);
											}}
										>
											<ColumnHeaderCell
												column={column}
												columnIndex={index}
												columnCount={columns.length}
												isPopoverOpen={openPopoverId === column.localId}
												onHeaderClick={() => openPopover(column.localId)}
												onContextMenu={(event) => {
													event.preventDefault();
													openContextMenu(column.localId, event.clientX, event.clientY);
												}}
												onPopoverClose={closePopover}
											/>
										</th>
									))}
								</SortableContext>
							</DndContext>
							<th role="columnheader" data-testid="ghost-add-column-cell">
								<GhostAddColumn
									columnCount={columns.length}
									onAdd={onAddColumn}
								/>
							</th>
						</tr>
					</thead>
					<tbody role="rowgroup">
						{renderBody ? renderBody(columns) : (defaultRows && defaultRows.length > 0
							? defaultRows
							: BLANK_ROWS
						).map((row, rowIndex) => (
							<tr
								key={rowIndex}
								role="row"
								className={exampleMode ? "italic text-gray-400" : undefined}
							>
								<td
									role="gridcell"
									style={STICKY_CELL_STYLE}
									data-testid={`row-number-${rowIndex + 1}`}
								>
									{rowIndex + 1}
								</td>
								{columns.map((column) => {
									// For description columns, prefer the per-column cell
									// value (cells[sortOrder]) so multi-description templates
									// show the right text in each column; fall back to
									// Cells are keyed by the column's stable localId so that
									// add / remove / reorder operations don't shift values onto
									// the wrong column. (Cells-only model — there is no
									// `row.name` slot.)
									const descriptionValue =
										column.columnType === "description"
											? row.cells?.[column.localId]
											: undefined;
									// Sample-value cell types should display in italics to
									// signal "this is placeholder text — supplier (or the
									// procurer, for description) will fill it in".
									const isSampleStyled =
										column.columnType === "description" ||
										column.columnType === "response_quantity" ||
										column.columnType === "response_value" ||
										column.columnType === "saving" ||
										column.columnType === "response_number" ||
										column.columnType === "response_text";
									const showingSample = !descriptionValue;
									return (
										<td
											key={column.localId}
											role="gridcell"
											data-testid={`preview-cell-${rowIndex + 1}-${column.localId}`}
											className={
												showingSample && isSampleStyled
													? "italic text-gray-500"
													: undefined
											}
										>
											{descriptionValue
												? descriptionValue
												: SAMPLE_CELL_LABELS[column.columnType]}
										</td>
									);
								})}
								<td role="gridcell" />
							</tr>
						))}
					</tbody>
				</Box>
			</Box>

			{showWarning && (
				<Text
					color="orange.500"
					fontSize="sm"
					mt={2}
					data-testid="column-count-warning"
				>
					You have {columns.length} columns. Tables with more than{" "}
					{COLUMN_WARNING_THRESHOLD} columns may be difficult for suppliers to
					fill in.
				</Text>
			)}

			{openPopoverColumn && (
				<ColumnEditPopover
					column={openPopoverColumn}
					isOpen
					onClose={closePopover}
					onUpdateLabel={(label) => onUpdateLabel(openPopoverColumn.localId, label)}
					onUpdateType={(type) => onUpdateType(openPopoverColumn.localId, type)}
					onDelete={() => {
						onRemoveColumn(openPopoverColumn.localId);
						closePopover();
					}}
					isDeleteDisabled={columns.length <= 1}
					isTypeDisabled={(type) =>
						isTypeDisabled(type, openPopoverColumn.columnType)
					}
					isTypeHidden={
						isTypeHidden
							? (type) => isTypeHidden(type, openPopoverColumn.columnType)
							: undefined
					}
					getDisabledReason={getDisabledReason}
					triggerRef={{
						current: headerRefs.current.get(openPopoverColumn.localId) ?? null,
					}}
				/>
			)}

			{contextMenuColumn && contextMenu && (
				<ColumnContextMenu
					anchorPosition={contextMenu.position}
					column={contextMenuColumn}
					columnCount={columns.length}
					columnIndex={contextMenuIndex}
					isOpen
					getDisabledReason={getDisabledReason}
					isTypeDisabled={(type, currentType) => isTypeDisabled(type, currentType)}
					isTypeHidden={
						isTypeHidden
							? (type, currentType) => isTypeHidden(type, currentType)
							: undefined
					}
					onChangeType={(type) =>
						onUpdateType(contextMenuColumn.localId, type)
					}
					onClose={closeContextMenu}
					onDelete={() => onRemoveColumn(contextMenuColumn.localId)}
					onDuplicate={() => onDuplicateColumn(contextMenuColumn.localId)}
					onEdit={() => openPopover(contextMenuColumn.localId)}
					onInsertLeft={() => onInsertColumn(contextMenuColumn.localId, "left")}
					onInsertRight={() => onInsertColumn(contextMenuColumn.localId, "right")}
					onMoveLeft={() => onMoveColumn(contextMenuColumn.localId, "left")}
					onMoveRight={() => onMoveColumn(contextMenuColumn.localId, "right")}
					data-testid="column-context-menu"
				/>
			)}
		</>
	);
};

export default SpreadsheetGrid;
